import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Check } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import {
  fetchCatalogoAreas,
  selecionarAreasCatalogo,
  type Area,
  type AreaCatalogo,
  type Configuracao,
} from '@/api/secretario';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { useAdvogadoEmEdicao } from './advogadoEmEdicao';
import { AreasManager } from './AreasManager';
import { PersonalizarAreas } from './PersonalizarAreas';
import { WizardActions } from './WizardActions';
import { useWizardSave } from './WizardSaveContext';
import { gravarConfigNoCache } from './cacheDaConfig';

interface StepProps {
  config: Configuracao | null;
  onNext: () => void;
  onBack: () => void;
  isFirst: boolean;
  /** No perfil do advogado: o que vem antes dos botões (o "Copiar áreas de…"). */
  inicio?: ReactNode;
}

/** Mesma comparação do backend (`catalogo.nome_comparavel`): sem acento, sem caixa. */
const nomeComparavel = (nome: string) =>
  nome
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/**
 * A área da configuração que corresponde a este item do catálogo: pela chave e,
 * na área antiga ou criada à mão, pelo nome — a MESMA regra do backend
 * (`config_service._area_do_catalogo`). Sem o nome, uma "Cível" criada à mão
 * aparecia desmarcada aqui e o PUT dos chips, que a acha pelo nome, a desligava.
 */
function areaDoCatalogo(config: Configuracao, item: AreaCatalogo): Area | undefined {
  return (
    config.areas.find((a) => a.catalogo_chave === item.chave) ??
    config.areas.find((a) => !a.catalogo_chave && nomeComparavel(a.nome) === nomeComparavel(item.nome))
  );
}

/** As chaves do catálogo cujas áreas estão ativas nesta configuração. */
function marcadasNoConfig(config: Configuracao, catalogo: AreaCatalogo[]): string[] {
  return catalogo.filter((item) => areaDoCatalogo(config, item)?.ativo).map((item) => item.chave);
}

/**
 * Passo 2 — em que áreas o escritório atua. Dois modos na mesma aba:
 *
 * - **chips** (o padrão): as áreas do catálogo, confirmadas num clique. Cada uma
 *   já traz as perguntas e os documentos do catálogo, e o Secretário começa
 *   fazendo o pré-atendimento nelas.
 * - **personalização**: honorários, modelos, base e cada área por dentro — o que
 *   faz o Secretário ir além do pré-atendimento. Opcional; nada aqui impede a
 *   ativação (docs/PLANO_SECRETARIO_ATIVACAO_SIMPLES.md).
 *
 * "Personalizar áreas" e "Cancelar" alternam entre os dois. A volta do editor de
 * modelos chega com ``?modo=personalizar`` e reabre na personalização.
 */
export function AreasAtendidasStep({ config, onNext, onBack, isFirst, inicio }: StepProps) {
  const queryClient = useQueryClient();
  const { showError } = useNotification();
  // No escritório, as áreas são as do advogado em edição (§4.3).
  const advogado = useAdvogadoEmEdicao();
  const [searchParams] = useSearchParams();
  const [personalizando, setPersonalizando] = useState(() => searchParams.get('modo') === 'personalizar');
  // Aberta uma vez, a personalização fica MONTADA (só escondida): o "Cancelar"
  // volta aos chips sem jogar fora o que foi digitado e não salvo — o auto-save
  // do wizard (que roda ao navegar) continua enxergando os cartões. É a mesma
  // regra dos passos do wizard, que nunca desmontam.
  const [personalizacaoMontada, setPersonalizacaoMontada] = useState(personalizando);
  const { data: catalogo, error, isLoading, refetch } = useQuery({
    queryKey: ['secretario', 'catalogo-areas'],
    queryFn: fetchCatalogoAreas,
  });

  // Configuração nova (sem área nenhuma): todas marcadas. Com áreas — inclusive
  // só as criadas à mão —, o que está ativo hoje: marcar tudo ali ligaria áreas
  // que o advogado nunca escolheu. ``marcadas`` só existe depois que a pessoa
  // mexe; antes, a seleção é derivada do config — e acompanha o que ela mudar
  // na personalização (ligar ou desligar uma área por dentro).
  // No escritório, quem marca pode ser o gestor, por outro advogado: nada vem marcado
  // sem ser escolhido (o "todas" é o atalho de quem configura o próprio Secretário).
  const jaEscolheu = config != null && (config.areas.length > 0 || advogado != null);
  const [marcadas, setMarcadas] = useState<Set<string> | null>(null);
  const selecionadas =
    marcadas ??
    new Set(
      config && jaEscolheu ? marcadasNoConfig(config, catalogo ?? []) : (catalogo ?? []).map((a) => a.chave),
    );

  // No perfil do advogado (uma tela só, sem Continuar), o toque já grava; a última
  // área desmarcada fica só na tela até ele marcar outra (sem área, ele sai da conversa).
  const alternar = (chave: string) => {
    const proximo = new Set(selecionadas);
    if (proximo.has(chave)) proximo.delete(chave);
    else proximo.add(chave);
    setMarcadas(proximo);
    // Inclusive a última: o backend diz quando não dá (o Secretário ativo sem área nenhuma).
    if (advogado) mutation.mutate([...proximo].sort());
  };

  const mutation = useMutation({
    mutationFn: (chaves: string[]) =>
      advogado ? selecionarAreasCatalogo(chaves, advogado.id) : selecionarAreasCatalogo(chaves),
    onSuccess: (atualizada) => {
      gravarConfigNoCache(queryClient, atualizada);
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });
      // Gravado: a seleção volta a ser derivada do config.
      setMarcadas(null);
    },
    onError: (err) => {
      // No perfil (grava no toque), a recusa devolve a tela ao que está gravado.
      if (advogado) setMarcadas(null);
      notifyMutationError(err, showError);
    },
  });

  const { mutate, isPending } = mutation;
  // Área fora do catálogo (nem pela chave, nem pelo nome) e ativa: com ela,
  // desmarcar todas as do catálogo ainda deixa o Secretário com onde pôr o caso.
  const doCatalogo = new Set(
    config && catalogo ? catalogo.map((item) => areaDoCatalogo(config, item)?.id).filter(Boolean) : [],
  );
  const temAvulsaAtiva = config?.areas.some((a) => a.ativo && !doCatalogo.has(a.id)) ?? false;
  const chaves = [...selecionadas].sort();
  const gravadas = config && catalogo ? marcadasNoConfig(config, catalogo).sort() : [];
  const dirty =
    config != null &&
    catalogo != null &&
    (chaves.length > 0 || temAvulsaAtiva) &&
    chaves.join(',') !== gravadas.join(',');
  // Auto-save ao navegar, só do que a pessoa MEXEU: a seleção padrão (todas
  // marcadas) só se grava pelo "Continuar". Sem isso, quem só passasse por este
  // passo — inclusive com o Secretário já ativo e áreas criadas à mão — ganharia
  // todas as áreas do catálogo ativas sem ter escolhido nada.
  const registerFlush = useWizardSave();
  const mexeu = marcadas !== null;
  useEffect(() => {
    registerFlush('areas-atendidas', mexeu && dirty && !isPending ? () => mutate(chaves) : null);
    return () => registerFlush('areas-atendidas', null);
    // ``chaves`` muda de identidade a cada render; o que importa é o conteúdo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mexeu, dirty, isPending, mutate, registerFlush, chaves.join(',')]);

  if (!config || isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size={32} label="Carregando" />
      </div>
    );
  }

  const continuar = () => {
    if (!dirty) {
      onNext();
      return;
    }
    mutation.mutate(chaves, { onSuccess: onNext });
  };

  const abrir = () => {
    setPersonalizacaoMontada(true);
    setPersonalizando(true);
  };
  // A seleção dos chips é gravada antes de abrir a personalização: é a partir
  // das áreas que ela existe, e a área recém-marcada precisa estar lá para ser
  // personalizada.
  const abrirPersonalizacao = () => {
    if (dirty) mutation.mutate(chaves, { onSuccess: abrir });
    else abrir();
  };

  const semSelecao = chaves.length === 0 && !temAvulsaAtiva;

  if (advogado) {
    return (
      <Card id="areas-do-advogado" tabIndex={-1} className="scroll-mt-20 outline-none">
        <CardHeader>
          <CardTitle>Áreas</CardTitle>
          <CardDescription>
            Toque para marcar. Cada área já faz o pré-atendimento; o lápis ajusta documentos, perguntas e
            honorários dela.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {inicio}
          {error != null && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>Não foi possível carregar as áreas: {classifyError(error).message}</AlertDescription>
            </Alert>
          )}
          <div className="flex flex-wrap gap-2" role="group" aria-label="Áreas de atuação">
            {(catalogo ?? []).map((area) => {
              const marcada = selecionadas.has(area.chave);
              return (
                <Button
                  key={area.chave}
                  type="button"
                  variant={marcada ? 'default' : 'outline'}
                  aria-pressed={marcada}
                  onClick={() => alternar(area.chave)}
                  disabled={isPending}
                  className="h-11 rounded-full px-4"
                >
                  {marcada && <Check className="mr-1.5 h-4 w-4" />}
                  {area.nome}
                </Button>
              );
            })}
          </div>
          <AreasManager config={config} compacto />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        {/* A saída da personalização fica no TOPO, onde se olha primeiro: a tela
            que se abre é bem mais densa que a dos chips, e quem a abriu por
            curiosidade precisa ver logo como voltar. */}
        {personalizando && (
          <Button
            type="button"
            variant="ghost"
            className="-ml-3 mb-1 min-h-11 self-start"
            onClick={() => setPersonalizando(false)}
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Cancelar
          </Button>
        )}
        <CardTitle>
          {personalizando ? 'Personalizar áreas' : 'Em que áreas você atua?'}
        </CardTitle>
        <CardDescription>
          {personalizando
            ? 'Honorários e modelos fazem o Secretário fechar contrato e procuração; perguntas e documentos ajustam o que ele pede em cada área. Tudo opcional.'
            : 'O Secretário identifica a área do caso, faz as perguntas certas e pede os documentos de cada uma. Desmarque as que você não atende.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {personalizacaoMontada && (
          <div hidden={!personalizando}>
            <PersonalizarAreas config={config} />
          </div>
        )}

        {!personalizando && (
          <>
            {error != null && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                  <span>Não foi possível carregar as áreas: {classifyError(error).message}</span>
                  <Button type="button" variant="outline" size="sm" onClick={() => refetch()}>
                    Tentar novamente
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap gap-2" role="group" aria-label="Áreas de atuação">
              {(catalogo ?? []).map((area) => {
                const marcada = selecionadas.has(area.chave);
                return (
                  <Button
                    key={area.chave}
                    type="button"
                    variant={marcada ? 'default' : 'outline'}
                    aria-pressed={marcada}
                    onClick={() => alternar(area.chave)}
                    // Travados durante o save: o sucesso volta a derivar a seleção
                    // do servidor, e um clique no meio dele se perderia.
                    disabled={isPending}
                    className="h-11 rounded-full px-4"
                  >
                    {marcada && <Check className="mr-1.5 h-4 w-4" />}
                    {area.nome}
                  </Button>
                );
              })}
            </div>

            <p className="text-xs text-muted-foreground">
              “Outros assuntos” recebe o que não se encaixa nas demais — você avalia caso a caso.
            </p>

            {semSelecao && catalogo != null && (
              <p className="text-sm text-destructive">Marque ao menos uma área.</p>
            )}

            <div className="flex justify-start">
              <Button
                type="button"
                variant="link"
                className="min-h-11 px-0"
                onClick={abrirPersonalizacao}
                disabled={isPending || semSelecao}
              >
                Personalizar áreas
              </Button>
            </div>
          </>
        )}

        <WizardActions
          isFirst={isFirst}
          onBack={onBack}
          primaryLabel="Continuar"
          onPrimary={continuar}
          // Na personalização a seleção não muda por aqui (as áreas se ligam no
          // próprio cartão): o "Continuar" só segue.
          primaryDisabled={!personalizando && (semSelecao || catalogo == null)}
          loading={mutation.isPending}
        />
      </CardContent>
    </Card>
  );
}
